import mdAxios from 'core/common/config/axios-config';
import {
  Invoice, IssueInvoicesDto, InvoiceTemplate,
  Payment, InvoiceTemplateItem, InvoiceItem, InvoiceGraph, PaymentRelation
} from 'invoice/domain/invoiceDomain';
import {IDomainObject} from "core/form/formUtils";
import {observable, action} from "mobx";
import {ClientGrid, GridParams} from "core/grid/domain/grid";
import {EVENTTYPE} from "core/event/eventDomain";
import {SelectableHolderWithPartyLov} from "party/partyDomain";
import {DocumentPreviewDto} from "core/document/documentDomain";
import { keys } from 'lodash';


export const invoiceItemGridLocation = 'invoiceItemList';
export const invoiceTemplateItemGridLocation = 'invoiceTemplateItemList';

class InvoiceService  {


  @observable public invoiceObject: Invoice;
  @observable public invoiceTemplateObject: InvoiceTemplate;


  readInvoice(invoiceId: number, params?: any): Promise<Invoice> {
    return mdAxios.get('/invoice/' + invoiceId, {params: params})
      .then(response => new Invoice(response.data));
  }

  issuedNew(paymentTypeId: number, billedToId: number): Promise<Invoice> {
    return mdAxios.get('/invoice/issued-new', {params: {paymentTypeId, billedToId}})
      .then(response => {
        let invoice = new Invoice(response.data);
        invoice.$new = true;
        return invoice;
      });
  }

  invoiceSave(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/save', invoice, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteInvoice(invoiceId: number): Promise<any> {
    return mdAxios.delete('/invoice/' + invoiceId, {showPrettyError: true});
  }

  recalculate(invoice: Invoice, params: {[key: string]: any}): Promise<IDomainObject> {
    return mdAxios.post('/invoice/recalculate', invoice, {params})
      .then((response) => response.data);
  }

  recalculateWithClean(invoice: Invoice, params: {[key: string]: any}): Promise<Invoice> {
    return this.recalculate(invoice, params)
      .then(action("recalculate-actionCb", (invoiceJson: IDomainObject) => {
        invoice.fromJson(invoiceJson);
        let grid = invoice.$movingBlocks.getBlock(invoiceItemGridLocation) as ClientGrid<InvoiceItem>;
        grid.search(invoice);
        return invoice;
      }))
  }

  prepareIssue(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/prepare-issue', invoice)
      .then((response) => response.data);

  }

  doIssue(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/do-issue', invoice, {showPrettyError: true})
      .then((response) => response.data);
  }

  previewInvoice(invoice: Invoice): Promise<DocumentPreviewDto> {
    return mdAxios.post('/invoice/preview', invoice, {showPrettyError: true})
      .then((response) => response.data);
  }


  cancelInvoice(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/cancel/' + invoice.invoiceId, {}, {showPrettyError: true})
      .then((response) => response.data);
  }

  reopenInvoice(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/reopen/' + invoice.invoiceId, {}, {showPrettyError: true})
      .then((response) => response.data);
  }



  addAllVehicles(invoice: Invoice): Promise<IDomainObject> {
    return mdAxios.post('/invoice/add-all-vehicles', invoice, {showPrettyError: true})
      .then((response) => response.data);
  }


  vatRateValue(vatRateId: number, validAt?: string|Date): Promise<number> {
    return mdAxios.get('/invoice/vat-rate-value/' + vatRateId, {params: {validAt}})
      .then(response => response.data);
  }


  invoiceNumberExists(spec): Promise<boolean> {
    return mdAxios.get('/invoice/invoice-exist', {params:spec})
      .then(response => response.data);
  }


  prepareMultipleIssue(gridParams: GridParams): Promise<IssueInvoicesDto> {
    return mdAxios.post('/invoice/prepare-issue-multiple', gridParams, {showPrettyError: true})
      .then(response => new IssueInvoicesDto(response.data));
  }

  doMultipleIssue(issueInvoicesObject: IssueInvoicesDto, part: number): Promise<IssueInvoicesDto> {
    return mdAxios.post('/invoice/issue-multiple/' + part, issueInvoicesObject, {showPrettyError: true})
      .then(response => new IssueInvoicesDto(response.data));
  }



  emailAddresses(invoiceObject: Invoice): Promise<SelectableHolderWithPartyLov<string>[]>  {
    return mdAxios.post('/invoice/email-addresses', invoiceObject)
      .then((response) => response.data);
  }


  sendInvoice(invoiceObject: Invoice, markOnly: boolean, insertSeparateDocsIntoMain: boolean, eventType: EVENTTYPE, emails: SelectableHolderWithPartyLov<string>[]): Promise<IDomainObject> {
    return mdAxios.post('/invoice/send/' + invoiceObject.invoiceId, {markOnly, insertSeparateDocsIntoMain, eventType, emails})
      .then((response) => response.data);
  }

  generateInvoiceFromTemplate(invoiceTemplateId: number, billedToId: number): Promise<Invoice> {
    return mdAxios.get('/invoice/from-template/' + invoiceTemplateId, {params: {billedToId}})
      .then(response => new Invoice(response.data));
  }


  // ////////// INVOICE TEMPLATE ////////////////////////////////////////////////////////////////////////////////

  readInvoiceTemplate(invoiceTemplateId: number, params?: any): Promise<InvoiceTemplate> {
    return mdAxios.get('/invoice-template/' + invoiceTemplateId, {params: params})
      .then(response => new InvoiceTemplate(response.data));
  }

  invoiceTemplateSave(invoiceTemplate: InvoiceTemplate): Promise<IDomainObject> {
    return mdAxios.post('/invoice-template/save', invoiceTemplate, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteInvoiceTemplate(invoiceTemplateId: number): Promise<any> {
    return mdAxios.delete('/invoice-template/' + invoiceTemplateId, {showPrettyError: true});
  }

  recalculateTemplate(invoiceTemplate: InvoiceTemplate, params: {[key: string]: any}): Promise<IDomainObject> {
    return mdAxios.post('/invoice-template/recalculate', invoiceTemplate, {params})
      .then((response) => response.data);
  }

  recalculateTemplateWithClean(invoiceTemplate: InvoiceTemplate, params: {[key: string]: any}): Promise<any> {
    return this.recalculateTemplate(invoiceTemplate, params)
      .then(action("recalculateTemplate-actionCb", (invoiceTemplateJson: IDomainObject) => {
        invoiceTemplate.fromJson(invoiceTemplateJson);
        let grid = invoiceTemplate.$movingBlocks.getBlock(invoiceTemplateItemGridLocation) as ClientGrid<InvoiceTemplateItem>;
        grid.search(invoiceTemplate);
      }))
  }



  // ////////// INVOICE PAYMENT ////////////////////////////////////////////////////////////////////////////////

  readInvoiceGraph(invoiceId: number): Promise<InvoiceGraph> {
    return mdAxios.get('/invoice-graph/' + invoiceId)
      .then(response => response.data);
  }

  saveInvoicePayment(payment: Payment, invoiceId: number): Promise<{invoice: Partial<Invoice>, payment: Partial<Payment>, paymentRelation: PaymentRelation}> {
    return mdAxios.post('/payment', payment, {showPrettyError: true, params: {invoiceId: invoiceId}})
      .then((response) => response.data);
  }

  deleteInvoicePayment(paymentId: number, invoiceId: number): Promise<IDomainObject> {
    return mdAxios.delete('/payment/' + paymentId, {showPrettyError: true, params: {invoiceId}})
      .then((response) => response.data);
  }

}

export default new InvoiceService();



// WEBPACK FOOTER //
// ./src/invoice/service/invoiceService.ts